import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, UserPlus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useI18n } from '../i18n/I18nProvider';
import { submitVisitor } from '../lib/api';

export default function VisitorCard() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', mobile: '', note: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: t.visitor.requiredTitle, description: t.visitor.requiredText });
      return;
    }
    setLoading(true);
    try {
      await submitVisitor(form);
      setForm({ name: '', email: '', mobile: '', note: '' });
      toast({ title: t.visitor.okTitle, description: t.visitor.okText });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="visitor" className="relative py-24 lg:py-32 bg-[#f5efe3]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="text-[#b88a2a] text-xs uppercase tracking-wider-xtra font-medium inline-flex items-center gap-2"><UserPlus size={14} /> {t.visitor.eyebrow}</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1f3a2a] mt-4 leading-[1.05]">{t.visitor.title_1}<br /><span className="italic">{t.visitor.title_2}</span></h2>
          <p className="mt-6 text-[#4a5a4e] text-lg leading-relaxed max-w-md">{t.visitor.sub}</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#e5dcc5] rounded-3xl p-7 lg:p-9 shadow-[0_20px_50px_-25px_rgba(31,58,42,0.25)] space-y-5">
          <div>
            <Label className="text-[#1f3a2a]">{t.visitor.name} <span className="text-[#b88a2a]">*</span></Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.visitor.yourName} className="mt-2 bg-[#faf6ea] border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]" />
          </div>
          <div>
            <Label className="text-[#1f3a2a]">{t.visitor.email} <span className="text-[#b88a2a]">*</span></Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.visitor.emailPh} className="mt-2 bg-[#faf6ea] border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]" />
          </div>
          <div>
            <Label className="text-[#1f3a2a]">{t.visitor.mobile} <span className="text-[#8a7a4e]">{t.visitor.optional}</span></Label>
            <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder={t.visitor.optional} className="mt-2 bg-[#faf6ea] border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]" />
          </div>
          <div>
            <Label className="text-[#1f3a2a]">{t.visitor.note}</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t.visitor.notePh} className="mt-2 bg-[#faf6ea] border-[#e5dcc5] rounded-xl focus-visible:ring-[#2e5340] min-h-[110px]" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#d4a63d] hover:bg-[#b88a2a] text-[#1f3a2a] rounded-full h-12 inline-flex items-center justify-center gap-2 font-medium">
            {loading ? t.visitor.sending : t.visitor.submit} <ArrowRight size={18} />
          </Button>
        </form>
      </div>
    </section>
  );
}
